/**
 * AssetGrid — right pane of the file manager.
 *
 * Grid/list toggle, search, sort, select-all, renders AssetCards.
 */

import { useState } from 'react'
import { Search, ChevronDown, Loader2 } from 'lucide-react'
import type { Asset, ViewMode, SortField, SortDir } from '../../types/fileManager'
import AssetCard from './AssetCard'
import ContextMenu, { MenuItem } from './ContextMenu'
import ViewToggle from '../ViewToggle'

interface AssetGridProps {
  assets: Asset[]
  total: number
  loading: boolean
  viewMode: ViewMode
  setViewMode: (m: ViewMode) => void
  searchQuery: string
  onSearchChange: (q: string) => void
  selectedIds: Set<number>
  onToggleSelect: (id: number, additive: boolean) => void
  onSelectAll: () => void
  onClearSelection: () => void
  onAssetContextMenu: (asset: Asset) => MenuItem[]
  onDragStart: (e: React.DragEvent, asset: Asset) => void
  onStatusChange?: (assetId: number, status: string) => void
  sortField: SortField
  sortDir: SortDir
  onSortChange: (field: SortField) => void
}

export default function AssetGrid({
  assets,
  total,
  loading,
  viewMode,
  setViewMode,
  searchQuery,
  onSearchChange,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  onAssetContextMenu,
  onDragStart,
  onStatusChange,
  sortField,
  sortDir,
  onSortChange,
}: AssetGridProps) {
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; items: MenuItem[] } | null>(null)

  const handleAssetCtx = (e: React.MouseEvent, asset: Asset) => {
    const items = onAssetContextMenu(asset)
    if (items.length > 0) {
      setCtxMenu({ x: e.clientX, y: e.clientY, items })
    }
  }

  const allSelected = assets.length > 0 && selectedIds.size === assets.length

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--rq-text-soft)]" />
          <input
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            placeholder="Search files..."
            className="w-full rq-input-dark text-sm pl-9 py-1.5"
          />
        </div>

        {/* Sort dropdown */}
        <div className="relative group">
          <button className="flex items-center gap-1 text-xs text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors px-2 py-1.5 rounded-lg hover:bg-white/5">
            Sort: {sortField === 'upload_time' ? 'Date' : sortField === 'original_filename' ? 'Name' : 'Size'}
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          <div className="absolute right-0 top-full mt-1 bg-[var(--rq-surface)] border border-white/15 rounded-lg shadow-lg py-1 hidden group-hover:block z-20 min-w-[120px]">
            {([['upload_time', 'Date'], ['original_filename', 'Name'], ['file_size', 'Size']] as [SortField, string][]).map(([field, label]) => (
              <button
                key={field}
                onClick={() => onSortChange(field)}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-white/5 transition-colors ${
                  sortField === field ? 'text-[var(--rq-text)]' : 'text-[var(--rq-text-soft)]'
                }`}
              >
                {label} {sortField === field && (sortDir === 'asc' ? '\u2191' : '\u2193')}
              </button>
            ))}
          </div>
        </div>

        {/* Select all / count */}
        <button
          onClick={allSelected ? onClearSelection : onSelectAll}
          className="text-xs text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
        >
          {selectedIds.size > 0 ? `${selectedIds.size} selected` : `${total} file${total !== 1 ? 's' : ''}`}
        </button>

        {/* View toggle */}
        <ViewToggle mode={viewMode} onChange={setViewMode} />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-[var(--rq-text-soft)] animate-spin" />
          </div>
        ) : assets.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-sm text-[var(--rq-text-soft)]">
              {searchQuery ? 'No files match your search' : 'No assets in this folder'}
            </p>
            <p className="text-xs text-[var(--rq-text-soft)] mt-1">
              {searchQuery ? 'Try a different search term' : 'Drag and drop files to upload'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {assets.map(asset => (
              <AssetCard
                key={asset.id}
                asset={asset}
                viewMode="grid"
                isSelected={selectedIds.has(asset.id)}
                onToggleSelect={onToggleSelect}
                onContextMenu={handleAssetCtx}
                onDragStart={onDragStart}
                onStatusChange={onStatusChange}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-1">
            {assets.map(asset => (
              <AssetCard
                key={asset.id}
                asset={asset}
                viewMode="list"
                isSelected={selectedIds.has(asset.id)}
                onToggleSelect={onToggleSelect}
                onContextMenu={handleAssetCtx}
                onDragStart={onDragStart}
                onStatusChange={onStatusChange}
              />
            ))}
          </div>
        )}
      </div>

      {/* Context menu */}
      {ctxMenu && (
        <ContextMenu x={ctxMenu.x} y={ctxMenu.y} items={ctxMenu.items} onClose={() => setCtxMenu(null)} />
      )}
    </div>
  )
}
